import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { tasksApi, usersApi, companiesApi } from '../api';
import { ArrowLeft, Pencil, Save, X, Trash2, CheckSquare, Building2, UserCircle, Target, KanbanSquare, Users } from 'lucide-react';
import SearchableSelect from '../components/SearchableSelect';
import MultiSearchableSelect from '../components/MultiSearchableSelect';
import dayjs from 'dayjs';

const priorityLabel: Record<string, string> = { LOW: 'Низкий', MEDIUM: 'Средний', HIGH: 'Высокий', URGENT: 'Срочно' };
const priorityColor: Record<string, string> = { LOW: 'badge-default', MEDIUM: 'badge-info', HIGH: 'badge-warning', URGENT: 'badge-danger' };
const statusLabel: Record<string, string> = { PENDING: 'Ожидает', IN_PROGRESS: 'В работе', COMPLETED: 'Выполнена', CANCELLED: 'Отменена' };
const statusColor: Record<string, string> = { PENDING: 'badge-warning', IN_PROGRESS: 'badge-info', COMPLETED: 'badge-success', CANCELLED: 'badge-default' };
const leadStatusLabel: Record<string, string> = { NEW: 'Новый', IN_WORK: 'В работе', QUALIFIED: 'Квалифицирован', CONVERTED: 'Конвертирован', REJECTED: 'Отклонен' };
const dealStageLabel: Record<string, string> = { LEAD: 'Новые', NEGOTIATION: 'Переговоры', PROPOSAL: 'Предложение', AGREEMENT: 'Согласование', WON: 'Успешно', LOST: 'Проиграна' };

export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await tasksApi.getById(id);
      setTask(res.data);
      setForm({
        title: res.data.title || '',
        description: res.data.description || '',
        status: res.data.status || 'PENDING',
        priority: res.data.priority || 'MEDIUM',
        dueDate: res.data.dueDate ? res.data.dueDate.split('T')[0] : '',
        dueTime: res.data.dueTime || '',
        assigneeId: res.data.assigneeId || '',
        companyId: res.data.companyId || '',
        participantIds: res.data.participants?.map((p: any) => p.id) || [],
        participantOrganizationIds: res.data.participantOrganizations?.map((o: any) => o.id) || [],
      });
    } catch (e) {
      console.error('Load task error:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [usersRes, compRes] = await Promise.all([
          usersApi.list({ limit: 100 }),
          companiesApi.list({ limit: 100 }),
        ]);
        setUsers(usersRes.data.data || []);
        setCompanies(compRes.data.data || []);
      } catch {}
    };
    loadDropdowns();
  }, []);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const data = {
        ...form,
        dueDate: form.dueDate || null,
        dueTime: form.dueTime || null,
        companyId: form.companyId || null,
        assigneeId: form.assigneeId || null,
        participantIds: form.participantIds,
        participantOrganizationIds: form.participantOrganizationIds,
      };
      await tasksApi.update(id, data);
      setEditing(false);
      load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Удалить задачу?')) return;
    try {
      await tasksApi.remove(id);
      navigate('/tasks');
    } catch (e) {
      alert('Ошибка удаления');
    }
  };

  const toggleComplete = async () => {
    if (!id || !task) return;
    try {
      await tasksApi.update(id, { isCompleted: !task.isCompleted });
      load();
    } catch (e) {
      alert('Ошибка обновления');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div>;
  if (!task) return <div style={{ padding: 40, textAlign: 'center' }}>Задача не найдена</div>;

  return (
    <div>
      <div className="detail-header">
        <button className="detail-back-btn" onClick={() => navigate('/tasks')}>
          <ArrowLeft size={20} />
        </button>
        <div className="detail-header-info">
          <div className="detail-header-title" style={{ textDecoration: task.isCompleted ? 'line-through' : 'none' }}>{task.title}</div>
          <div className="detail-header-subtitle">
            <span className={`badge ${statusColor[task.status] || 'badge-default'}`}>{statusLabel[task.status] || task.status}</span>
            <span className={`badge ${priorityColor[task.priority] || 'badge-default'}`} style={{ marginLeft: 6 }}>{priorityLabel[task.priority] || task.priority}</span>
          </div>
        </div>
        <div className="detail-header-actions">
          {editing ? (
            <>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                <Save size={16} /> {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); load(); }}>
                <X size={16} /> Отмена
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary btn-sm" onClick={toggleComplete}>
                <CheckSquare size={16} /> {task.isCompleted ? 'Вернуть в работу' : 'Выполнена'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
                <Pencil size={16} /> Редактировать
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                <Trash2 size={16} /> Удалить
              </button>
            </>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-grid-main">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Информация о задаче</div>
            </div>
            <div className="card-body">
              {editing ? (
                <>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Название *</label>
                      <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <SearchableSelect
                        label="Исполнитель"
                        value={form.assigneeId}
                        onChange={val => setForm({ ...form, assigneeId: val })}
                        options={users.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
                        emptyLabel="— Выберите —"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Приоритет</label>
                      <select className="form-select" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                        <option value="LOW">Низкий</option>
                        <option value="MEDIUM">Средний</option>
                        <option value="HIGH">Высокий</option>
                        <option value="URGENT">Срочно</option>
                      </select>
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Статус</label>
                      <select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                        <option value="PENDING">Ожидает</option>
                        <option value="IN_PROGRESS">В работе</option>
                        <option value="COMPLETED">Выполнена</option>
                        <option value="CANCELLED">Отменена</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Срок</label>
                      <input className="form-input" type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Время</label>
                      <input className="form-input" type="time" value={form.dueTime} onChange={e => setForm({ ...form, dueTime: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <SearchableSelect
                        label="Связанная компания"
                        value={form.companyId}
                        onChange={val => setForm({ ...form, companyId: val })}
                        options={companies.map((c: any) => ({ value: c.id, label: c.name }))}
                        emptyLabel="— Не связана —"
                      />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Описание</label>
                      <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <MultiSearchableSelect
                        label="Участники"
                        value={form.participantIds}
                        onChange={val => setForm({ ...form, participantIds: val })}
                        options={users.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
                      />
                    </div>
                    <div className="form-group">
                      <MultiSearchableSelect
                        label="Организации-участники"
                        value={form.participantOrganizationIds}
                        onChange={val => setForm({ ...form, participantOrganizationIds: val })}
                        options={companies.map((c: any) => ({ value: c.id, label: c.name }))}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="detail-field-grid">
                  <div className="detail-field detail-field-full">
                    <div className="detail-field-label">Название</div>
                    <div className="detail-field-value" style={{ fontWeight: 600, textDecoration: task.isCompleted ? 'line-through' : 'none' }}>{task.title}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Статус</div>
                    <div className="detail-field-value"><span className={`badge ${statusColor[task.status] || 'badge-default'}`}>{statusLabel[task.status] || task.status}</span></div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Приоритет</div>
                    <div className="detail-field-value"><span className={`badge ${priorityColor[task.priority] || 'badge-default'}`}>{priorityLabel[task.priority] || task.priority}</span></div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Исполнитель</div>
                    <div className="detail-field-value">{task.assignee ? `${task.assignee.firstName} ${task.assignee.lastName}` : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Создал</div>
                    <div className="detail-field-value">{task.createdBy ? `${task.createdBy.firstName} ${task.createdBy.lastName}` : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Срок</div>
                    <div className="detail-field-value">{task.dueDate ? dayjs(task.dueDate).format('DD.MM.YYYY') : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Время</div>
                    <div className="detail-field-value">{task.dueTime || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field detail-field-full">
                    <div className="detail-field-label">Описание</div>
                    <div className="detail-field-value">{task.description || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Выполнена</div>
                    <div className="detail-field-value">{task.isCompleted ? `Да, ${task.completedAt ? dayjs(task.completedAt).format('DD.MM.YYYY HH:mm') : ''}` : 'Нет'}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Создано</div>
                    <div className="detail-field-value">{dayjs(task.createdAt).format('DD.MM.YYYY HH:mm')}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="detail-grid-sidebar">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Связанные сущности</div>
            </div>
            <div className="card-body" style={{ padding: 12 }}>
              {task.company && (
                <Link to={`/companies/${task.company.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title"><Building2 size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{task.company.name}</div>
                    <div className="detail-related-item-meta">Компания</div>
                  </div>
                </Link>
              )}
              {task.contact && (
                <Link to={`/contacts/${task.contact.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title"><UserCircle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{task.contact.firstName} {task.contact.lastName}</div>
                    <div className="detail-related-item-meta">Контакт</div>
                  </div>
                </Link>
              )}
              {task.lead && (
                <Link to={`/leads/${task.lead.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title"><Target size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{task.lead.firstName} {task.lead.lastName}</div>
                    <div className="detail-related-item-meta">Лид · {leadStatusLabel[task.lead.status] || task.lead.status}</div>
                  </div>
                </Link>
              )}
              {task.deal && (
                <Link to={`/deals/${task.deal.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title"><KanbanSquare size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{task.deal.title}</div>
                    <div className="detail-related-item-meta">Сделка · {dealStageLabel[task.deal.stage] || task.deal.stage}</div>
                  </div>
                </Link>
              )}
              {task.participants?.length > 0 && task.participants.map((p: any) => (
                <Link key={p.id} to={`/users/${p.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title"><Users size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{p.firstName} {p.lastName}</div>
                    <div className="detail-related-item-meta">Участник</div>
                  </div>
                </Link>
              ))}
              {task.participantOrganizations?.length > 0 && task.participantOrganizations.map((o: any) => (
                <Link key={o.id} to={`/companies/${o.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title"><Building2 size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{o.name}</div>
                    <div className="detail-related-item-meta">Организация-участник</div>
                  </div>
                </Link>
              ))}
              {!task.company && !task.contact && !task.lead && !task.deal && !task.participants?.length && !task.participantOrganizations?.length && (
                <div className="detail-related-empty">Нет связанных сущностей</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
